import React, { useEffect } from 'react';

import invariant from 'tiny-invariant';

import { fg } from '@atlaskit/platform-feature-flags';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { useInViewport } from '../../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../../common/utils/use-nav-analytics';
import useScrollDirection from '../../../common/utils/use-scroll-direction';
import { useOptions } from '../../../controllers/options-provider';
import { useUserSideNavDataStore } from '../../../controllers/user-state/user-side-nav-data-store-provider';
import {
	SideNavVisibilityProvider,
	useSideNavVisibility,
} from '../../../controllers/visibility/use-nav-visibility';
import { SideNavigationLayout } from '../index';
import type { GSNLayoutProps } from '../types';

/**
 * GSN SideNavigationLayout - A layout component that implements the base side navigation structure.
 *
 * It utilizes the GSN context to support different functionalities such as Drag and Drop,
 * analytics, user state persistence, and side navigation visibility management.
 *
 */
export function GSNSideNavigationLayout(props: GSNLayoutProps) {
	const { setUserSideNavData } = useUserSideNavDataStore();

	return (
		<SideNavVisibilityProvider setUserSideNavData={setUserSideNavData}>
			<GSNLayoutComponent {...props} />
		</SideNavVisibilityProvider>
	);
}

function GSNLayoutComponent({
	preMainElem,
	postMainElem,
	bottomElem,
	children,
	testId,
}: GSNLayoutProps) {
	const { fireEvent } = useAnalyticsEvents();
	const { userSideNavData } = useUserSideNavDataStore();
	const { onCollapse, onExpand, onResizeStart, onResizeEnd } = useSideNavVisibility();
	const { l1DragAndDropEnabled } = useOptions();
	const sideNavContentRef = React.useRef<HTMLDivElement>(null);
	const scrollDirection = useScrollDirection(sideNavContentRef);
	const { isInViewport } = useInViewport({ ref: sideNavContentRef, rootMargin: '0px' });
	const { fireViewedSideNavEvent } = useNavAnalytics();

	useEffect(() => {
		if (scrollDirection) {
			fireEvent('ui.sideNav.scrolled.sideNavMenu', {
				scrollDirection,
			});
		}
	}, [fireEvent, scrollDirection]);

	useEffect(() => {
		if (!l1DragAndDropEnabled) {
			return;
		}

		const scrollable = sideNavContentRef.current;
		invariant(scrollable, 'sideNavContentRef must be set');
		return autoScrollForElements({
			element: scrollable,
			canScroll: ({ source }) => source.data.type === 'menu-item',
		});
	}, [l1DragAndDropEnabled]);

	useEffect(() => {
		if (isInViewport && fg('navx-2558-fix-sidenav-viewed-event')) {
			fireViewedSideNavEvent();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInViewport]);

	return (
		<SideNavigationLayout
			testId={testId}
			onExpand={onExpand}
			onCollapse={onCollapse}
			defaultWidth={userSideNavData.defaultWidth}
			defaultCollapsed={userSideNavData.defaultCollapsed}
			sideNavContentRef={sideNavContentRef}
			preMainElem={preMainElem}
			postMainElem={postMainElem}
			onResizeEnd={onResizeEnd}
			onResizeStart={onResizeStart}
			bottomElem={bottomElem}
		>
			{children}
		</SideNavigationLayout>
	);
}
